import { useEffect } from 'react'

/**
 * Executes the provided callback in the `setter` for the input's `value`
 * property. If the callback returns a value it will be set as the input's value.
 *
 * This is useful for cases where you need to autoformat the input's value or
 * perform some side effect when it is mutated directly via the DOM.
 */
export function useValueSetter (
  ref: React.RefObject<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  callback: (value: string | number) => string | number | void,
  dependencies: unknown[] = []
) {
  useEffect(() => {
    const input = ref.current

    if (input) {
      const prototype = input.constructor.prototype
      const originalDescriptor =
        Object.getOwnPropertyDescriptor(prototype, 'value') ||
        {}

      // Disabling this eslint rule because the getter is not missing. It's in
      // the `originalDescriptor`, but the eslint rule doesn't know that.
      // eslint-disable-next-line accessor-pairs
      Object.defineProperty(input, 'value', {
        ...originalDescriptor,
        set: function (value) {
          const nextValue = callback(value)
          return originalDescriptor.set?.call(this, nextValue ?? value)
        },
        configurable: true,
      })

      return () => {
        Object.defineProperty(input, 'value', originalDescriptor)
      }
    }
  }, dependencies)
}
